import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, EmptyState, Modal, Field, Input, Textarea, SelectBox, Button,
} from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import { Plus, Star, Pencil, Trash2 } from "lucide-react";

const EMPTY = { client_id: "", customer_name: "", service: "", rating: 5, comment: "", date: new Date().toISOString().slice(0, 10) };

function Stars({ value, onChange, size = 18 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type={onChange ? "button" : undefined} onClick={onChange ? () => onChange(n) : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star size={size} className={n <= value ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#e0d6ef]"} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { t } = useI18n();

  const load = () => api.get("/reviews").then(({ data }) => setReviews(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/services").then(({ data }) => setServices(data.map((s) => s.name)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (r) => { setForm(r); setEditing(r.id); setOpen(true); };

  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, client_id: cid, customer_name: c.name }));
    else set("client_id", "");
  };

  const save = async () => {
    if (!form.customer_name) return toast.error(t("Customer name is required"));
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editing) await api.put(`/reviews/${editing}`, payload);
      else await api.post("/reviews", payload);
      toast.success(editing ? t("Review updated") : t("Review added"));
      setOpen(false); load();
    } catch { toast.error("Could not save review"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/reviews/${id}`); toast.success(t("Review deleted")); load();
  };

  const avg = reviews.length ? (reviews.reduce((a, r) => a + Number(r.rating), 0) / reviews.length).toFixed(1) : "0.0";

  return (
    <div data-testid="reviews-page">
      <PageHeader title="Reviews" subtitle="Log and showcase client feedback for your services."
        action={<PrimaryButton onClick={openNew} data-testid="add-review-button"><Plus size={16} /> {t("Add Review")}</PrimaryButton>} />

      {reviews.length > 0 && (
        <div className="mt-card p-6 mb-6 flex items-center gap-6">
          <div className="text-center">
            <div className="font-display text-4xl font-bold text-[#5A189A]">{avg}</div>
            <Stars value={Math.round(avg)} size={16} />
          </div>
          <div className="text-sm text-[#52525B]">{t("Average rating across")} <span className="font-semibold text-[#121212]">{reviews.length}</span> {t("reviews")}</div>
        </div>
      )}

      {reviews.length === 0 ? (
        <EmptyState icon={Star} title="No reviews yet" subtitle="Capture client feedback and ratings to build social proof."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("Add Review")}</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-grid">
          {reviews.map((r) => (
            <div key={r.id} className="mt-card p-5 group">
              <div className="flex items-start justify-between">
                <Stars value={r.rating} />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <IconBtn onClick={() => openEdit(r)} title="Edit"><Pencil size={15} /></IconBtn>
                  <IconBtn onClick={() => remove(r.id)} title="Delete" danger><Trash2 size={15} /></IconBtn>
                </div>
              </div>
              <p className="text-sm text-[#52525B] mt-3 italic min-h-[48px]">"{r.comment || "No comment provided."}"</p>
              <div className="mt-4 pt-3 border-t border-[#f4f0fa]">
                <div className="font-semibold text-sm text-[#121212]">{r.customer_name}</div>
                <div className="text-xs text-[#9b8bb5]">{r.service} · {r.date}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Review" : "Add Review"} size="md"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-review-button">{t("Save Review")}</PrimaryButton></>}>
        <div className="space-y-4">
          <Field label="Client"><SelectBox value={form.client_id} onChange={(e) => pickClient(e.target.value)} options={clients.map((c) => ({ value: c.id, label: c.name }))} placeholder="Select client" /></Field>
          <Field label="Customer Name *"><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="review-name-input" /></Field>
          <Field label="Service"><SelectBox value={form.service} onChange={(e) => set("service", e.target.value)} options={services} placeholder="Select service" /></Field>
          <Field label="Rating"><Stars value={Number(form.rating)} onChange={(n) => set("rating", n)} size={24} /></Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <Field label="Comment"><Textarea rows={3} value={form.comment} onChange={(e) => set("comment", e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}
